import { DOCUMENT } from '@angular/common';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { environment } from '@env';

export interface GtmConfig {
  /** Google Tag Manager container id; empty disables Google Tag Manager. */
  containerId: string;
}

export const GTM_CONFIG = new InjectionToken<GtmConfig>('GTM_CONFIG', {
  providedIn: 'root',
  factory: () => environment.gtm,
});

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };

/**
 * Loads the Google Tag Manager container named in the environment config. Only the production
 * build sets an id, so every other build sends nothing to Google Analytics.
 */
@Injectable({ providedIn: 'root' })
export class GtmLoader {
  private readonly config = inject(GTM_CONFIG);
  private readonly document = inject(DOCUMENT);

  private loaded = false;

  /** Loads the container once; does nothing when the id is empty or there is no window. */
  load(): void {
    const { containerId } = this.config;
    const window = this.document.defaultView as DataLayerWindow | null;
    if (this.loaded || !containerId || !window) return;
    this.loaded = true;

    window.dataLayer ??= [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    this.document.head.appendChild(script);
  }
}
